import { useEffect, useState } from "react";
import { useNotify } from "ra-core";
import { Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

import { pruefe, sb } from "./daten";
import type { KontoArt, Steuerjahr, Takt } from "./kern/typen";
import {
  Abschnitt,
  Auswahl,
  Feld,
  Hinweis,
  Tabelle,
  fmtDatum,
  type TabProps,
} from "./teile";

type Zahlfeld = {
  [K in keyof Steuerjahr]: Steuerjahr[K] extends number ? K : never;
}[keyof Steuerjahr];

const ZAHLFELDER: [Zahlfeld, string][] = [
  ["satz_regel", "Steuersatz regulär (%)"],
  ["satz_ermaessigt", "Steuersatz ermäßigt (%)"],
  ["km_satz", "Geschäftsfahrt je km (€)"],
  ["pendel_satz", "Wohnung–Betrieb je Entfernungs-km (€)"],
  ["entfernung_km", "Entfernung Wohnung–Betrieb einfach (km)"],
  ["homeoffice_tag", "Homeoffice je Tag (€)"],
  ["homeoffice_max", "Homeoffice je Jahr höchstens (€)"],
  ["anteil_telefon", "Anteil Telefon (%)"],
  ["anteil_kabel", "Anteil Kabel (%)"],
  ["grenze_rechnung", "Prüfgrenze Rechnung brutto (€)"],
  ["grenze_gwg", "GWG-Grenze netto (€)"],
];

export const SetupTab = ({ jahr, basis, daten, neuLaden }: TabProps) => {
  const notify = useNotify();
  const [form, setForm] = useState<Steuerjahr>(basis.jahr);
  useEffect(() => setForm(basis.jahr), [basis.jahr]);
  const setze = <K extends keyof Steuerjahr>(k: K, w: Steuerjahr[K]) =>
    setForm((f) => ({ ...f, [k]: w }));

  const schreiben = async (
    aktion: () => PromiseLike<{ error: { message: string } | null }>,
    ok?: string,
  ) => {
    try {
      pruefe(await aktion());
      await neuLaden();
      if (ok) notify(ok);
    } catch (e) {
      notify(e instanceof Error ? e.message : String(e), { type: "error" });
    }
  };

  const speichern = () =>
    schreiben(
      () =>
        sb()
          .from("bh_steuerjahr")
          .update({ ...form, updated_at: new Date().toISOString() })
          .eq("jahr", jahr),
      "Setup gespeichert",
    );

  const [takt, setTakt] = useState<{ gueltig_ab: string; takt: Takt }>({
    gueltig_ab: `${jahr}-01-01`,
    takt: "offen",
  });
  const [konto, setKonto] = useState<{
    name: string;
    iban: string;
    art: KontoArt;
  }>({ name: "", iban: "", art: "geschaeftlich" });
  const bindung = form.kur_verzicht_ab
    ? Number(form.kur_verzicht_ab.slice(0, 4)) + 4
    : null;

  return (
    <div className="space-y-4">
      <Abschnitt
        titel={`Steuerjahr ${jahr}`}
        text="Alle Berechnungen lesen von hier."
      >
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Feld name="Rechtsform">
            <Input
              value={form.rechtsform}
              onChange={(e) => setze("rechtsform", e.target.value)}
            />
          </Feld>
          <Feld name="Gewinnermittlung">
            <Input
              value={form.gewinnermittlung}
              onChange={(e) => setze("gewinnermittlung", e.target.value)}
            />
          </Feld>
          <Feld name="Besteuerung">
            <Auswahl
              value={form.besteuerung}
              onChange={(e) =>
                setze(
                  "besteuerung",
                  e.target.value as Steuerjahr["besteuerung"],
                )
              }
            >
              <option value="ist">Ist (vereinnahmte Entgelte)</option>
              <option value="soll">Soll (vereinbarte Entgelte)</option>
            </Auswahl>
          </Feld>
          <Feld name="Tätigkeitsbeginn">
            <Input
              type="date"
              value={form.taetigkeitsbeginn ?? ""}
              onChange={(e) =>
                setze("taetigkeitsbeginn", e.target.value || null)
              }
            />
          </Feld>
          <Feld name="Verzicht auf Kleinunternehmerregelung seit">
            <Input
              type="date"
              value={form.kur_verzicht_ab ?? ""}
              onChange={(e) => setze("kur_verzicht_ab", e.target.value || null)}
            />
          </Feld>
          <div className="flex items-center gap-6 pt-5">
            <label className="flex items-center gap-2 text-sm">
              <Switch
                checked={form.kur_verzicht}
                onCheckedChange={(w) => setze("kur_verzicht", w)}
              />{" "}
              Verzicht
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch
                checked={form.dauerfrist}
                onCheckedChange={(w) => setze("dauerfrist", w)}
              />{" "}
              Dauerfrist
            </label>
          </div>
        </div>
        {form.kur_verzicht && bindung && (
          <Hinweis>
            Der Verzicht bindet fünf Kalenderjahre, also bis 31.12.{bindung}.
          </Hinweis>
        )}
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {ZAHLFELDER.map(([k, name]) => (
            <Feld key={k} name={name}>
              <Input
                type="number"
                step="any"
                value={form[k]}
                onChange={(e) => setze(k, Number(e.target.value))}
              />
            </Feld>
          ))}
        </div>
        {jahr <= 2025 && (
          <Hinweis>
            Pendlersatz für Dezember 2025 ist mit 0,30 € vorbelegt. Bitte
            prüfen.
          </Hinweis>
        )}
        <Button onClick={speichern}>Speichern</Button>
      </Abschnitt>

      <Abschnitt
        titel="UStVA-Takt"
        text="Mit gültig-ab, der Takt kann sich im Jahr ändern. Ohne Eintrag gilt offen."
      >
        {daten.takte.length > 0 && (
          <Tabelle kopf={["Gültig ab", "Takt", ""]}>
            {daten.takte.map((t) => (
              <tr key={t.id}>
                <td>{fmtDatum(t.gueltig_ab)}</td>
                <td>
                  {t.takt === "vierteljaehrlich" ? "vierteljährlich" : t.takt}
                </td>
                <td className="text-right">
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Takt löschen"
                    onClick={() =>
                      schreiben(() =>
                        sb()
                          .from("bh_ustva_takt")
                          .delete()
                          .eq("id", t.id ?? 0),
                      )
                    }
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </Tabelle>
        )}
        <div className="flex flex-wrap items-end gap-2">
          <Feld name="Gültig ab">
            <Input
              type="date"
              value={takt.gueltig_ab}
              onChange={(e) => setTakt({ ...takt, gueltig_ab: e.target.value })}
            />
          </Feld>
          <Feld name="Takt">
            <Auswahl
              value={takt.takt}
              onChange={(e) =>
                setTakt({ ...takt, takt: e.target.value as Takt })
              }
            >
              <option value="offen">offen</option>
              <option value="monatlich">monatlich</option>
              <option value="vierteljaehrlich">vierteljährlich</option>
              <option value="befreit">befreit</option>
            </Auswahl>
          </Feld>
          <Button
            variant="outline"
            onClick={() =>
              schreiben(() =>
                sb()
                  .from("bh_ustva_takt")
                  .upsert({ ...takt, jahr }, { onConflict: "jahr,gueltig_ab" }),
              )
            }
          >
            Eintragen
          </Button>
        </div>
      </Abschnitt>

      <Abschnitt
        titel="Berechtigte Konten"
        text="Gelten jahresübergreifend. Zeilen von Privatkonten werden einzeln durchgesehen."
      >
        {daten.konten.length > 0 && (
          <Tabelle kopf={["Name", "IBAN", "Art", ""]}>
            {daten.konten.map((k) => (
              <tr key={k.id}>
                <td>{k.name}</td>
                <td className="font-mono text-xs">{k.iban}</td>
                <td>{k.art === "privat" ? "privat" : "geschäftlich"}</td>
                <td className="text-right">
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Konto löschen"
                    onClick={() =>
                      schreiben(() =>
                        sb().from("bh_konto").delete().eq("id", k.id),
                      )
                    }
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </Tabelle>
        )}
        <div className="flex flex-wrap items-end gap-2">
          <Feld name="Name">
            <Input
              value={konto.name}
              onChange={(e) => setKonto({ ...konto, name: e.target.value })}
            />
          </Feld>
          <Feld name="IBAN" className="min-w-64">
            <Input
              value={konto.iban}
              onChange={(e) => setKonto({ ...konto, iban: e.target.value })}
            />
          </Feld>
          <Feld name="Art">
            <Auswahl
              value={konto.art}
              onChange={(e) =>
                setKonto({ ...konto, art: e.target.value as KontoArt })
              }
            >
              <option value="geschaeftlich">geschäftlich</option>
              <option value="privat">privat</option>
            </Auswahl>
          </Feld>
          <Button
            variant="outline"
            disabled={!konto.name || !konto.iban}
            onClick={async () => {
              await schreiben(() =>
                sb()
                  .from("bh_konto")
                  .insert({
                    ...konto,
                    iban: konto.iban.replace(/\s/g, "").toUpperCase(),
                  }),
              );
              setKonto({ name: "", iban: "", art: "geschaeftlich" });
            }}
          >
            Konto anlegen
          </Button>
        </div>
      </Abschnitt>
    </div>
  );
};
